import { useEffect, useRef, useState } from 'react'
import type { Map as MapboxMap, Marker, MapMouseEvent } from 'mapbox-gl'
import { MapPin } from 'lucide-react'
import { getEnv } from '@/lib/env'
import { Input } from '@/shared/components/ui/input'

/** Centro por defecto: La Habana (cuando no hay coordenadas aún). */
const HABANA = { lng: -82.3666, lat: 23.1136 }
const DEFAULT_ZOOM = 11
const PIN_ZOOM = 15
const MARKER_COLOR = '#f97316'

interface LocationMapFieldProps {
  latitude?: string | null
  longitude?: string | null
  /** Recibe lat/lng como string (6 decimales). Ausente ⇒ solo-lectura. */
  onChange?: (latitude: string, longitude: string) => void
  readOnly?: boolean
}

function parseCoord(value?: string | null): number | null {
  if (value == null || value.trim() === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Mapa para ver/fijar la ubicación geográfica de una dirección. Usa Mapbox GL
 * (lazy-import). Sin `VITE_MAPBOX_TOKEN` degrada a inputs manuales de lat/lng
 * (o a texto en solo-lectura), nunca rompe. En edición, tocar el mapa coloca el
 * marcador y emite las coordenadas.
 */
export function LocationMapField({
  latitude,
  longitude,
  onChange,
  readOnly,
}: LocationMapFieldProps) {
  const token = getEnv('VITE_MAPBOX_TOKEN')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapboxMap | null>(null)
  const markerRef = useRef<Marker | null>(null)
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])
  const [failed, setFailed] = useState(false)

  const lat = parseCoord(latitude)
  const lng = parseCoord(longitude)

  useEffect(() => {
    if (!token || !containerRef.current) return
    let cancelled = false
    let map: MapboxMap | undefined

    void (async () => {
      try {
        const mapboxgl = (await import('mapbox-gl')).default
        await import('mapbox-gl/dist/mapbox-gl.css')
        if (cancelled || !containerRef.current) return
        mapboxgl.accessToken = token
        const hasPin = lat != null && lng != null
        map = new mapboxgl.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: hasPin ? [lng, lat] : [HABANA.lng, HABANA.lat],
          zoom: hasPin ? PIN_ZOOM : DEFAULT_ZOOM,
        })
        mapRef.current = map
        if (hasPin) {
          markerRef.current = new mapboxgl.Marker({ color: MARKER_COLOR })
            .setLngLat([lng, lat])
            .addTo(map)
        }
        if (!readOnly) {
          map.on('click', (e: MapMouseEvent) => {
            const { lng: clng, lat: clat } = e.lngLat
            if (markerRef.current) {
              markerRef.current.setLngLat([clng, clat])
            } else if (map) {
              markerRef.current = new mapboxgl.Marker({ color: MARKER_COLOR })
                .setLngLat([clng, clat])
                .addTo(map)
            }
            onChangeRef.current?.(clat.toFixed(6), clng.toFixed(6))
          })
        }
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()

    return () => {
      cancelled = true
      map?.remove()
      mapRef.current = null
      markerRef.current = null
    }
    // Init único; la sincronización de coords externas va en el efecto de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, readOnly])

  // Refleja en el marcador las coordenadas que lleguen desde fuera (reset del
  // form, carga diferida). No re-centra agresivo para no pelear con el usuario.
  useEffect(() => {
    const map = mapRef.current
    if (!map || lat == null || lng == null) return
    void (async () => {
      const mapboxgl = (await import('mapbox-gl')).default
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat])
      } else {
        markerRef.current = new mapboxgl.Marker({ color: MARKER_COLOR })
          .setLngLat([lng, lat])
          .addTo(map)
      }
    })()
  }, [lat, lng])

  // Sin token o fallo del mapa: degradación.
  if (!token || failed) {
    if (readOnly) {
      return lat != null && lng != null ? (
        <p className="text-xs text-ink-muted">
          Ubicación: {lat.toFixed(5)}, {lng.toFixed(5)}
        </p>
      ) : (
        <p className="text-xs text-ink-subtle">Sin ubicación en el mapa.</p>
      )
    }
    return (
      <div className="space-y-2">
        <p className="text-xs text-ink-subtle">
          Mapa no disponible. Introduce las coordenadas manualmente.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Input
            inputMode="decimal"
            placeholder="Latitud"
            value={latitude ?? ''}
            onChange={(e) => onChange?.(e.target.value, longitude ?? '')}
          />
          <Input
            inputMode="decimal"
            placeholder="Longitud"
            value={longitude ?? ''}
            onChange={(e) => onChange?.(latitude ?? '', e.target.value)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <div
        ref={containerRef}
        className="h-56 w-full overflow-hidden rounded-card border border-line"
      />
      <p className="flex items-center gap-1 text-xs text-ink-muted">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        {lat != null && lng != null
          ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
          : readOnly
            ? 'Sin ubicación fijada.'
            : 'Toca el mapa para fijar la ubicación.'}
      </p>
    </div>
  )
}
